import React, { useEffect } from 'react';
import { Achievement } from '../types';

interface AchievementModalProps {
  achievement: Achievement | null;
  onClose: () => void;
}

const AchievementModal: React.FC<AchievementModalProps> = ({ achievement, onClose }) => {
  useEffect(() => {
    if (achievement) {
      // Auto-close after 3 seconds
      const timer = setTimeout(onClose, 3000);
      return () => clearTimeout(timer);
    }
  }, [achievement, onClose]);

  if (!achievement) return null;

  const categoryColors = {
    quest: 'from-blue-500 to-blue-600',
    level: 'from-purple-500 to-purple-600',
    streak: 'from-orange-500 to-red-600',
    stat: 'from-green-500 to-green-600',
    special: 'from-yellow-500 to-yellow-600',
  };

  return (
    <div className="fixed inset-0 backdrop-blur-xl flex items-center justify-center z-50 p-3 sm:p-4 modal-backdrop animate-fade-in">
      {/* Radial glow effect */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="w-[500px] h-[500px] bg-yellow-500/20 rounded-full blur-3xl animate-pulse"></div>
      </div>

      <div className="relative animate-level-up max-w-sm w-full">
        {/* Main Modal */}
        <div className="bg-gradient-to-b from-slate-800/50 via-slate-900/50 to-slate-950/50 backdrop-blur-md rounded-3xl corner-decoration overflow-hidden border-t-2 border-l-2 border-yellow-500/40 border-r-2 border-b-2 border-r-yellow-700/60 border-b-yellow-700/60 animate-scale-in"
          style={{
            boxShadow: '0 8px 24px rgba(234, 179, 8, 0.6), 0 2px 8px rgba(234, 179, 8, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
          }}
        >
          <div className="relative p-6 sm:p-8 text-center">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-5">
              {[...Array(8)].map((_, i) => (
                <div
                  key={i}
                  className="absolute h-px bg-gradient-to-r from-transparent via-yellow-500 to-transparent"
                  style={{
                    top: `${i * 12.5}%`,
                    left: 0,
                    right: 0,
                    animation: `pulse ${2 + i * 0.2}s ease-in-out infinite`,
                  }}
                />
              ))}
            </div>

            {/* Header */}
            <div className="mb-3 sm:mb-4">
              <div className="text-xs sm:text-sm text-yellow-400 font-orbitron tracking-widest uppercase mb-1 sm:mb-2">
                ［ Achievement Unlocked! ］
              </div>
            </div>

            {/* Icon */}
            <div className="relative mb-4 sm:mb-5">
              {achievement.image ? (
                <img 
                  src={achievement.image} 
                  alt={achievement.title}
                  className="w-20 h-20 sm:w-24 sm:h-24 object-contain mb-1.5 sm:mb-2 animate-float-3d mx-auto"
                />
              ) : (
                <div className="text-5xl sm:text-7xl mb-1.5 sm:mb-2 animate-float-3d">{achievement.icon}</div>
              )}
              <div className={`inline-block px-3 sm:px-4 py-0.5 sm:py-1 bg-gradient-to-r ${categoryColors[achievement.category]} rounded-full text-[10px] sm:text-xs font-orbitron font-bold uppercase tracking-wider`}>
                {achievement.category}
              </div>
            </div>

            {/* Title & Description */}
            <div className="mb-4 sm:mb-6">
              <h2 className="text-2xl sm:text-3xl font-black font-orbitron glow-text mb-2 sm:mb-3 tracking-wider">
                {achievement.title}
              </h2>
              <p className="text-gray-300 font-rajdhani text-base sm:text-lg">
                {achievement.description}
              </p>
            </div>

            {/* Progress Indicator */}
            <div className="bg-gradient-to-b from-yellow-950/40 via-slate-950/30 to-yellow-950/40 backdrop-blur-sm border-t-2 border-l-2 border-yellow-500/40 border-r-2 border-b-2 border-r-yellow-700/60 border-b-yellow-700/60 rounded-2xl p-4 sm:p-6 mb-4 sm:mb-6"
              style={{
                boxShadow: '0 4px 12px rgba(234, 179, 8, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.1), inset 0 -2px 4px rgba(0, 0, 0, 0.4)'
              }}
            >
              <div className="flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 text-yellow-400">
                <span className="text-xl sm:text-2xl">✨</span>
                <span className="font-orbitron font-bold text-sm sm:text-base">{achievement.requirement}</span>
                <span className="text-xs sm:text-sm font-rajdhani">milestone reached</span>
              </div>
            </div>

            {/* Continue Button */}
            <button
              onClick={onClose}
              className="relative group px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-b from-yellow-600/90 to-orange-600/90 backdrop-blur-sm border-t-2 border-l-2 border-yellow-500/50 border-r-2 border-b-2 border-r-orange-700/70 border-b-orange-700/70 rounded-2xl font-orbitron font-bold text-sm sm:text-base tracking-wider transition-all hover:scale-105 hover:shadow-[0_0_20px_rgba(234,179,8,0.5)] overflow-hidden"
              style={{
                boxShadow: '0 4px 12px rgba(234, 179, 8, 0.4), inset 0 1px 2px rgba(255, 255, 255, 0.2)'
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/20 rounded-2xl"></div>
              <span className="relative z-10">CONTINUE</span>
            </button>

            {/* Auto-close indicator */}
            <div className="mt-2 sm:mt-3 text-[10px] sm:text-xs text-gray-500 font-rajdhani">
              Auto-closing in 3 seconds...
            </div>
          </div>
        </div>

        {/* Decorative corners */}
        <div className="absolute -top-2 -left-2 sm:-top-4 sm:-left-4 w-6 h-6 sm:w-8 sm:h-8 border-t-2 border-l-2 sm:border-t-4 sm:border-l-4 border-yellow-500 animate-pulse"></div>
        <div className="absolute -top-2 -right-2 sm:-top-4 sm:-right-4 w-6 h-6 sm:w-8 sm:h-8 border-t-2 border-r-2 sm:border-t-4 sm:border-r-4 border-yellow-500 animate-pulse"></div>
        <div className="absolute -bottom-2 -left-2 sm:-bottom-4 sm:-left-4 w-6 h-6 sm:w-8 sm:h-8 border-b-2 border-l-2 sm:border-b-4 sm:border-l-4 border-yellow-500 animate-pulse"></div>
        <div className="absolute -bottom-2 -right-2 sm:-bottom-4 sm:-right-4 w-6 h-6 sm:w-8 sm:h-8 border-b-2 border-r-2 sm:border-b-4 sm:border-r-4 border-yellow-500 animate-pulse"></div>
      </div>
    </div>
  );
};

export default AchievementModal;

